import i18n from '@/i18n';
import { exportToTemplate } from '@/utils/exportUtils';
import { imageStorageAPI } from '@/utils/api';
import {
  addDownloadCenterRecord,
  estimateClientZipBytes,
  patchDownloadCenterRecord,
  queueBrowserZipDownload,
  shouldUseServerZipDownload,
  submitServerDownloadTask,
  triggerBrowserFileDownload,
} from '@/utils/downloadCenter';
import { fetchImageBlob } from './goodsZipDownload';
import { resolveListingTemplateIds } from './workflowInteractive';

function sanitizeZipPart(name) {
  return String(name || '').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'file';
}

/** 导出/ZIP 文件名前缀：不用标题模板全文，避免 ZIP 里出现整段 prompt */
function resolveWorkflowExportStampName(row, titlesStep) {
  const idPart = `条目${row?.id ?? ''}`;
  const raw = String(titlesStep?.titleText || '').trim();
  if (!raw || raw.length > 60 || /助手|模板|请严|优化|以下内容|电商标题/.test(raw)) {
    return idPart;
  }
  const short = sanitizeZipPart(raw).slice(0, 40);
  return short || idPart;
}

const WORKFLOW_EXPORT_CLAIM_MS = 120_000;

/** 最近一次工作流 ZIP（供完成后「再次下载」；仅保留最近几条避免占内存） */
const workflowZipCache = new Map();
const WORKFLOW_ZIP_CACHE_MAX = 5;

function rememberWorkflowZip(taskId, blob, fileName) {
  const key = String(taskId || '_last');
  workflowZipCache.set(key, { blob, fileName, at: Date.now() });
  workflowZipCache.set('_last', { blob, fileName, at: Date.now() });
  if (workflowZipCache.size > WORKFLOW_ZIP_CACHE_MAX + 1) {
    const sorted = [...workflowZipCache.entries()]
      .filter(([k]) => k !== '_last')
      .sort((a, b) => (b[1].at || 0) - (a[1].at || 0));
    sorted.slice(WORKFLOW_ZIP_CACHE_MAX).forEach(([k]) => workflowZipCache.delete(k));
  }
}

/** 工作流完成后用户可手动再次触发浏览器保存（仅当前页内存有效，刷新后需重新生成） */
export function downloadCachedWorkflowZip(taskId) {
  const key = taskId ? String(taskId) : '_last';
  const hit = workflowZipCache.get(key) || workflowZipCache.get('_last');
  if (!hit?.blob) return false;
  return triggerBrowserFileDownload(hit.blob, hit.fileName, {
    dedupeScope: `retry-${key}-${Date.now()}`,
    skipDedupe: true,
  });
}

function isWorkflowRowListingExportable(entry, expectedRunId) {
  const { result } = entry || {};
  if (!result?.ok || result?.skipped) return false;
  if (
    expectedRunId != null &&
    result.workflowRunId != null &&
    result.workflowRunId !== expectedRunId
  ) {
    return false;
  }
  const steps = result.steps || [];
  if (steps.some((s) => s.navId === 'platformListing' && s.status === 'ok')) {
    return true;
  }
  const goods = steps.find((s) => s.navId === 'goods');
  return (
    Array.isArray(goods?.compositeImageUrls) &&
    goods.compositeImageUrls.some((u) => u && String(u).trim())
  );
}

function collectExportableWorkflowResults(task) {
  const entries =
    Array.isArray(task?.results) && task.results.length > 0
      ? task.results
      : Array.isArray(task?.summary?.results)
        ? task.summary.results
        : [];
  const expectedRunId = task?.workflowRunId ?? null;
  return entries.filter((entry) => isWorkflowRowListingExportable(entry, expectedRunId));
}

/**
 * 下载 ZIP：优先用内存缓存；刷新页面后按任务结果重新打包并触发浏览器保存
 */
export async function downloadWorkflowListingZipForTask(task, template) {
  const taskId = String(task?.id || '');
  if (downloadCachedWorkflowZip(taskId)) {
    return { ok: true, source: 'cache', zipName: workflowZipCache.get(taskId)?.fileName };
  }

  if (!isListingTemplateExportMode(task, template)) {
    return { ok: false, error: i18n.t('workbench:ipeUtilError.exportModeDisabled') };
  }

  const exportable = collectExportableWorkflowResults(task);
  if (exportable.length === 0) {
    return {
      ok: false,
      error: i18n.t('workbench:ipeUtilError.noExportSuccessItems'),
    };
  }

  const listingTemplateIds = resolveListingTemplateIds(task, template);
  try {
    const res = await exportWorkflowListingResults(
      exportable,
      listingTemplateIds,
      taskId,
      task?.workflowRunId ?? null,
    );
    return {
      ok: true,
      source: 'regenerated',
      zipName: res.bundle?.zipName,
      rowCount: res.rowCount,
    };
  } catch (e) {
    return { ok: false, error: e.message || String(e) };
  }
}

/** 同一工作流任务短时间只合并导出一次（防多标签页 / hydrate 重复执行） */
function claimWorkflowListingExport(taskId) {
  if (!taskId || typeof sessionStorage === 'undefined') return true;
  const key = `ipe-wf-listing-export-${taskId}`;
  try {
    const raw = sessionStorage.getItem(key);
    const at = raw ? Number(raw) : 0;
    if (at && Date.now() - at < WORKFLOW_EXPORT_CLAIM_MS) return false;
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

function collectWorkflowExportImages(exportData) {
  const seen = new Set();
  const items = [];
  for (const row of exportData || []) {
    const prefix = sanitizeZipPart(row?.stampName || `条目${row?.id ?? ''}`);
    (row.compositeImages || []).forEach((img, index) => {
      const url = img?.url;
      if (!url || seen.has(url)) return;
      seen.add(url);
      items.push({
        url,
        fileName: `${prefix}_结果图${index + 1}.jpg`,
      });
    });
    const stampUrl = row?.stampImageUrl;
    if (stampUrl && !seen.has(stampUrl)) {
      seen.add(stampUrl);
      items.push({ url: stampUrl, fileName: `${prefix}_印花.jpg` });
    }
  }
  return items;
}

async function downloadWorkflowExportBundle({ excelFiles, exportData, taskId = '' }) {
  const images = collectWorkflowExportImages(exportData);
  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  const zipName = `工作流上架_${stamp}.zip`;
  const imageZipName = `工作流上架图片_${stamp}.zip`;

  // 大批量/大体积图片强制服务端打包；Excel 体积小，单独走客户端 ZIP
  if (shouldUseServerZipDownload({
    itemCount: images.length,
    estimatedBytes: estimateClientZipBytes({ itemCount: images.length }),
  })) {
    const written = new Set();
    const serverItems = [];
    for (const img of images) {
      const base = sanitizeZipPart(img.fileName);
      const path = written.has(base) ? base.replace(/(\.[^.]+)?$/, `_${written.size}$1`) : base;
      written.add(path);
      serverItems.push({ url: img.url, path: `图片/${path}` });
    }

    if (serverItems.length > 0) {
      await submitServerDownloadTask({
        fileName: imageZipName,
        source: '工作流导出',
        items: serverItems,
        required: true,
      });
    }

    if ((excelFiles || []).length > 0) {
      const JSZip = (await import('jszip')).default;
      const excelZip = new JSZip();
      const excelWritten = new Set();
      for (const file of excelFiles || []) {
        const name = sanitizeZipPart(file?.fileName || '上架表格.xlsx');
        const path = excelWritten.has(name)
          ? `${name.replace(/\.xlsx$/i, '')}_${excelWritten.size}.xlsx`
          : name;
        excelWritten.add(path);
        excelZip.file(`表格/${path}`, file.buffer);
      }
      const excelBlob = await excelZip.generateAsync({ type: 'blob' });
      const excelZipName = `工作流上架表格_${stamp}.zip`;
      const excelRecordId = `dl-${Date.now()}-wf-xlsx`;
      addDownloadCenterRecord({
        id: excelRecordId,
        fileName: excelZipName,
        url: '',
        source: '工作流导出',
        status: 'downloading',
        createdAt: new Date().toISOString(),
        completedAt: null,
        downloadedAt: null,
        error: null,
        itemCount: excelFiles.length,
        progress: 90,
      });
      await queueBrowserZipDownload({
        blob: excelBlob,
        fileName: excelZipName,
        source: '工作流导出',
        recordId: excelRecordId,
        meta: { itemCount: excelFiles.length },
      });
    }

    return {
      zipName: imageZipName,
      excelCount: excelFiles?.length || 0,
      imageCount: images.length,
      downloadTriggered: false,
      serverSide: true,
    };
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const written = new Set();

  for (const file of excelFiles || []) {
    const name = sanitizeZipPart(file?.fileName || '上架表格.xlsx');
    const path = written.has(name) ? `${name.replace(/\.xlsx$/i, '')}_${written.size}.xlsx` : name;
    written.add(path);
    zip.file(`表格/${path}`, file.buffer);
  }

  for (const img of images) {
    try {
      const blob = await fetchImageBlob(img.url);
      const base = sanitizeZipPart(img.fileName);
      const path = written.has(base) ? base.replace(/(\.[^.]+)?$/, `_${written.size}$1`) : base;
      written.add(path);
      zip.file(`图片/${path}`, blob);
    } catch (e) {
      console.warn('[workflow export] 打包图片失败:', img.fileName, e);
    }
  }

  const blob = await zip.generateAsync({ type: 'blob' });

  const recordId = `dl-${Date.now()}-wf`;
  addDownloadCenterRecord({
    id: recordId,
    fileName: zipName,
    url: '',
    source: '工作流导出',
    status: 'downloading',
    createdAt: new Date().toISOString(),
    completedAt: null,
    downloadedAt: null,
    error: null,
    itemCount: images.length,
    progress: 90,
  });

  try {
    rememberWorkflowZip(taskId, blob, zipName);
    await queueBrowserZipDownload({
      blob,
      fileName: zipName,
      source: '工作流导出',
      recordId,
      meta: { itemCount: images.length },
    });
  } catch (e) {
    patchDownloadCenterRecord(recordId, {
      status: 'failed',
      error: String(e?.message || e),
      completedAt: new Date().toISOString(),
    });
    throw e;
  }

  return {
    zipName,
    excelCount: excelFiles?.length || 0,
    imageCount: images.length,
    downloadTriggered: false,
  };
}

async function persistWorkflowExportImageUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  if (raw.startsWith('data:')) {
    try {
      const result = await imageStorageAPI.save(raw, { folder: 'image-space' });
      const saved = result?.url || '';
      return saved || '';
    } catch (e) {
      console.warn('[workflow export] 套图上传失败:', e);
      return raw;
    }
  }
  return raw;
}

/** 从流水线结果组装上架模版导出行 */
export async function buildWorkflowListingExportData(results, expectedRunId = null) {
  if (!Array.isArray(results)) return [];
  const rows = results.filter((entry) => isWorkflowRowListingExportable(entry, expectedRunId));
  const built = [];
  for (const { row, result } of rows) {
    const steps = result?.steps || [];
    const stamp = steps.find((s) => s.navId === 'stampExtract');
    const goods = steps.find((s) => s.navId === 'goods');
    const titles = steps.find((s) => s.navId === 'titles' || s.navId === 'titleExtract' || s.navId === 'aiCopywriting');
    const compositeImages = [];

    if (Array.isArray(goods?.compositeImageUrls)) {
      for (let i = 0; i < goods.compositeImageUrls.length; i += 1) {
        const url = goods.compositeImageUrls[i];
        if (!url) continue;
        const persisted = await persistWorkflowExportImageUrl(url);
        compositeImages.push({
          url: persisted || url,
          name: `套图${i + 1}`,
          imageId: '',
        });
      }
    }

    built.push({
      id: row?.id,
      title: titles?.titleText || titles?.copywritingText || row?.title || `条目 ${row?.id ?? ''}`,
      titleLanguage: titles?.language || result?.copywritingLanguage || '',
      stampName: resolveWorkflowExportStampName(row, titles),
      stampImageUrl: stamp?.outputImageUrl || result?.initialImageUrl || row?.primaryImageUrl || '',
      stampImageId: stamp?.stampOutputSpaceId || '',
      compositeImages,
    });
  }
  return built.filter((row) => row.stampImageUrl || row.compositeImages.length > 0 || row.id);
}

export async function exportWorkflowListingResults(results, listingTemplateIds, taskId = '', expectedRunId = null) {
  const ids = (listingTemplateIds || []).map(String).filter(Boolean);
  if (ids.length === 0) return { exported: false, imageDownloadCount: 0 };
  const exportData = await buildWorkflowListingExportData(results, expectedRunId);
  if (exportData.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.noExportSuccessStamp'));
  }
  const { excelFiles, imageDownloadCount } = await exportToTemplate(exportData, ids, null, {
    runImageDownloads: false,
    deferExcelDownloads: true,
  });
  const bundle = await downloadWorkflowExportBundle({
    excelFiles,
    exportData,
    taskId,
  });
  return {
    exported: true,
    rowCount: exportData.length,
    imageDownloadCount,
    bundle,
    templateCount: ids.length,
  };
}

function isListingTemplateExportMode(task, template) {
  const ids = resolveListingTemplateIds(task, template);
  if (ids.length === 0) return false;
  const fromRuntime = task?.runtimeStepOverrides?.platformListing;
  const step = (template?.steps || []).find((s) => s.navId === 'platformListing');
  const autoJob = fromRuntime?.autoCreateListingJob ?? step?.params?.autoCreateListingJob;
  return autoJob !== true;
}

/** 任务全部完成或终止后，将已完成上架步骤的条目合并为一次导出（避免每条触发一次浏览器下载） */
export async function finalizeWorkflowListingExport(allResults, task, template, taskIdOverride = '') {
  if (!isListingTemplateExportMode(task, template)) {
    return null;
  }
  const taskId = String(taskIdOverride || task?.id || '');
  if (task?.summary?.exportSummary?.exported) {
    return task.summary.exportSummary;
  }
  if (taskId && !claimWorkflowListingExport(taskId)) {
    return task?.summary?.exportSummary || { exported: false, skippedDuplicate: true };
  }
  const listingTemplateIds = resolveListingTemplateIds(task, template);
  const expectedRunId = task?.workflowRunId ?? null;
  const exportable = (allResults || []).filter((entry) =>
    isWorkflowRowListingExportable(entry, expectedRunId),
  );
  if (exportable.length === 0) {
    return null;
  }
  try {
    const res = await exportWorkflowListingResults(
      exportable,
      listingTemplateIds,
      taskId,
      expectedRunId,
    );
    return {
      exported: true,
      rowCount: res.rowCount,
      imageDownloadCount: res.imageDownloadCount,
      bundle: res.bundle,
      templateCount: res.templateCount,
      exportedAt: new Date().toISOString(),
    };
  } catch (e) {
    return { exported: false, error: e.message || String(e), rowCount: exportable.length };
  }
}
